import React, { useMemo, useRef, useEffect, useState } from 'react';
import './CityMap.css';

export interface TrafficLight {
  id: string;
  x: number;
  y: number;
  state: 'red' | 'yellow' | 'green';
}

export interface Vehicle {
  id: string;
  routeId: string;
  progress: number; // 0-1 auf der Route
  speed: number;    // Aktuelle Geschwindigkeit
  maxSpeed: number; // Maximale Geschwindigkeit
  color: string;
}

export interface StopPoint {
  intersectionId: string;
  progress: number;        // Position 0-1 auf der Route
  direction: 'ns' | 'ew' | 'ring' | 'diagonal';  // Welche Ampel-Richtung relevant ist
}

export interface Route {
  id: string;
  path: string; // SVG path d
  stopPoints: StopPoint[];  // Alle Kreuzungen die diese Route passiert
}

// Vordefinierte Routen durch die Stadt
export const routes: Route[] = [
  // Nord-Süd durch östlichen Altstadtring
  {
    id: 'ns-east',
    path: 'M 600 50 L 600 280 Q 700 280 720 350 Q 740 420 720 490 Q 700 530 600 530 L 600 750',
    stopPoints: [
      { intersectionId: 'inner-n', progress: 0.30, direction: 'ns' },  // Einfahrt von Nord-Radiale
      { intersectionId: 'inner-s', progress: 0.70, direction: 'ew' },  // Ausfahrt vom Ring (von Ost kommend)
    ]
  },
  // Süd-Nord durch westlichen Altstadtring
  {
    id: 'sn-west',
    path: 'M 600 750 L 600 530 Q 500 530 480 490 Q 460 420 480 350 Q 500 280 600 280 L 600 50',
    stopPoints: [
      { intersectionId: 'inner-s', progress: 0.30, direction: 'ns' },  // Einfahrt von Süd-Radiale
      { intersectionId: 'inner-n', progress: 0.70, direction: 'ew' },  // Ausfahrt vom Ring (von West kommend)
    ]
  },

  // West-Ost über nördlichen Altstadtring (folgt exakt dem Ring-Pfad)
  {
    id: 'we-north',
    path: 'M 50 400 L 200 400 L 468 400 Q 470 375 480 350 Q 500 280 600 280 Q 700 280 720 350 Q 730 375 732 400 L 1000 400 L 1150 400',
    stopPoints: [
      { intersectionId: 'outer-w', progress: 0.13, direction: 'ew' },
      { intersectionId: 'inner-w', progress: 0.37, direction: 'ew' },   // Einfahrt von West-Radiale
      { intersectionId: 'inner-n', progress: 0.50, direction: 'ew' },   // Durchfahrt auf Ring (von West kommend)
      { intersectionId: 'inner-e', progress: 0.63, direction: 'ns' },   // Ausfahrt zu Ost-Radiale (vom Ring kommend)
      { intersectionId: 'outer-e', progress: 0.87, direction: 'ew' },
    ]
  },
  // Ost-West über südlichen Altstadtring (folgt exakt dem Ring-Pfad)
  {
    id: 'ew-south',
    path: 'M 1150 400 L 1000 400 L 732 400 Q 730 425 720 490 Q 700 530 600 530 Q 500 530 480 490 Q 470 425 468 400 L 200 400 L 50 400',
    stopPoints: [
      { intersectionId: 'outer-e', progress: 0.13, direction: 'ew' },
      { intersectionId: 'inner-e', progress: 0.37, direction: 'ew' },   // Einfahrt von Ost-Radiale
      { intersectionId: 'inner-s', progress: 0.50, direction: 'ew' },   // Durchfahrt auf Ring (von Ost kommend)
      { intersectionId: 'inner-w', progress: 0.63, direction: 'ns' },   // Ausfahrt zu West-Radiale (vom Ring kommend)
      { intersectionId: 'outer-w', progress: 0.87, direction: 'ew' },
    ]
  },

  // Mittlerer Ring Nord
  {
    id: 'ring-n-we',
    path: 'M 920 200 Q 760 100 600 100 Q 440 100 280 200',
    stopPoints: [
      { intersectionId: 'outer-ne', progress: 0.03, direction: 'ring' },
      { intersectionId: 'outer-n', progress: 0.50, direction: 'ew' },
      { intersectionId: 'outer-nw', progress: 0.97, direction: 'ring' },
    ]
  },
  {
    id: 'ring-n-ew',
    path: 'M 280 200 Q 440 100 600 100 Q 760 100 920 200',
    stopPoints: [
      { intersectionId: 'outer-nw', progress: 0.03, direction: 'ring' },
      { intersectionId: 'outer-n', progress: 0.50, direction: 'ew' },
      { intersectionId: 'outer-ne', progress: 0.97, direction: 'ring' },
    ]
  },

  // Mittlerer Ring Süd
  {
    id: 'ring-s-we',
    path: 'M 920 600 Q 760 700 600 700 Q 440 700 280 600',
    stopPoints: [
      { intersectionId: 'outer-se', progress: 0.03, direction: 'ring' },
      { intersectionId: 'outer-s', progress: 0.50, direction: 'ew' },
      { intersectionId: 'outer-sw', progress: 0.97, direction: 'ring' },
    ]
  },
  {
    id: 'ring-s-ew',
    path: 'M 280 600 Q 440 700 600 700 Q 760 700 920 600',
    stopPoints: [
      { intersectionId: 'outer-sw', progress: 0.03, direction: 'ring' },
      { intersectionId: 'outer-s', progress: 0.50, direction: 'ew' },
      { intersectionId: 'outer-se', progress: 0.97, direction: 'ring' },
    ]
  },

  // A9 Tunnel (Nord-Süd durch Tunnel) - keine Ampeln
  {
    id: 'a9-ns',
    path: 'M 600 35 L 600 100 Q 660 180 665 300 L 665 480 Q 660 620 600 700 L 600 765',
    stopPoints: []
  },
  {
    id: 'a9-sn',
    path: 'M 600 765 L 600 700 Q 660 620 665 480 L 665 300 Q 660 180 600 100 L 600 35',
    stopPoints: []
  },

  // Diagonalen
  {
    id: 'nw-diagonal',
    path: 'M 150 150 Q 215 175 280 200',
    stopPoints: [
      { intersectionId: 'outer-nw', progress: 0.92, direction: 'diagonal' },
    ]
  },
  {
    id: 'ne-diagonal',
    path: 'M 1050 150 Q 985 175 920 200',
    stopPoints: [
      { intersectionId: 'outer-ne', progress: 0.92, direction: 'diagonal' },
    ]
  },
  {
    id: 'sw-diagonal',
    path: 'M 150 650 Q 215 625 280 600',
    stopPoints: [
      { intersectionId: 'outer-sw', progress: 0.92, direction: 'diagonal' },
    ]
  },
  {
    id: 'se-diagonal',
    path: 'M 1050 650 Q 985 625 920 600',
    stopPoints: [
      { intersectionId: 'outer-se', progress: 0.92, direction: 'diagonal' },
    ]
  },

  // Autobahn A99 Umfahrung - keine Ampeln
  {
    id: 'a99-n',
    path: 'M 35 400 L 35 35 L 600 35 L 1165 35 L 1165 400',
    stopPoints: []
  },
  {
    id: 'a99-s',
    path: 'M 1165 400 L 1165 765 L 600 765 L 35 765 L 35 400',
    stopPoints: []
  },
];

interface CityMapProps {
  trafficLights?: TrafficLight[];
  vehicles?: Vehicle[];
  onIntersectionClick?: (id: string) => void;
}

export default function CityMap({ trafficLights = [], vehicles = [], onIntersectionClick }: CityMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [pathLengths, setPathLengths] = useState<Record<string, number>>({});

  // Berechne Pfadlängen beim Mounten
  useEffect(() => {
    if (!svgRef.current) return;

    const lengths: Record<string, number> = {};
    routes.forEach(route => {
      const path = svgRef.current?.querySelector(`#route-${route.id}`) as SVGPathElement;
      if (path) {
        lengths[route.id] = path.getTotalLength();
      }
    });
    setPathLengths(lengths);
  }, []);

  // Funktion um Position auf Pfad zu berechnen
  const getPositionOnPath = (routeId: string, progress: number): { x: number; y: number; angle: number } | null => {
    if (!svgRef.current) return null;

    const path = svgRef.current.querySelector(`#route-${routeId}`) as SVGPathElement;
    if (!path) return null;

    const length = pathLengths[routeId] || path.getTotalLength();
    const point = path.getPointAtLength(progress * length);

    // Berechne Winkel für Rotation
    const delta = 0.01;
    const point2 = path.getPointAtLength(Math.min(progress + delta, 1) * length);
    const angle = Math.atan2(point2.y - point.y, point2.x - point.x) * (180 / Math.PI);

    return { x: point.x, y: point.y, angle };
  };

  const getTrafficLightColors = (id: string): { ns: string; ew: string } => {
    const light = trafficLights.find(l => l.id === id);
    if (!light) return { ns: '#888', ew: '#888' };

    // N-S Richtung bekommt den Hauptzustand, E-W das Gegenteil
    switch (light.state) {
      case 'green': return { ns: '#44ff44', ew: '#ff4444' };
      case 'red': return { ns: '#ff4444', ew: '#44ff44' };
      case 'yellow': return { ns: '#ffcc00', ew: '#ffcc00' };
      default: return { ns: '#888', ew: '#888' };
    }
  };

  // Für Eck-Kreuzungen: Ring vs Diagonale
  const getCornerTrafficLightColors = (id: string): { ring: string; diagonal: string } => {
    const light = trafficLights.find(l => l.id === id);
    if (!light) return { ring: '#888', diagonal: '#888' };

    // Ring-Richtungen zusammen, Diagonale separat
    switch (light.state) {
      case 'green': return { ring: '#44ff44', diagonal: '#ff4444' };
      case 'red': return { ring: '#ff4444', diagonal: '#44ff44' };
      case 'yellow': return { ring: '#ffcc00', diagonal: '#ffcc00' };
      default: return { ring: '#888', diagonal: '#888' };
    }
  };

  // Gebäudeblöcke mit stabilen Keys (kein Math.random bei jedem Render)
  const buildings = useMemo(() => {
    const result: React.ReactElement[] = [];
    const seed = 12345;
    let seedState = seed;

    const seededRandom = () => {
      seedState = (seedState * 1103515245 + 12345) & 0x7fffffff;
      return seedState / 0x7fffffff;
    };

    const generateBuildings = (x: number, y: number, w: number, h: number, density: number = 0.7) => {
      const blockSize = 25;
      const gap = 8;

      for (let bx = x + gap; bx < x + w - blockSize; bx += blockSize + gap) {
        for (let by = y + gap; by < y + h - blockSize; by += blockSize + gap) {
          if (seededRandom() < density) {
            const bw = blockSize - 4 + seededRandom() * 8;
            const bh = blockSize - 4 + seededRandom() * 8;
            result.push(
              <rect
                key={`b-${bx}-${by}`}
                x={bx}
                y={by}
                width={bw}
                height={bh}
                fill="#2a2a3e"
                opacity={0.6 + seededRandom() * 0.3}
                rx="2"
              />
            );
          }
        }
      }
    };

    generateBuildings(80, 80, 180, 180, 0.6);
    generateBuildings(940, 80, 180, 180, 0.6);
    generateBuildings(80, 540, 180, 180, 0.6);
    generateBuildings(940, 540, 180, 180, 0.6);
    generateBuildings(140, 280, 120, 240, 0.8);
    generateBuildings(940, 280, 120, 240, 0.8);
    generateBuildings(300, 60, 250, 100, 0.7);
    generateBuildings(800, 60, 120, 100, 0.7);
    generateBuildings(300, 640, 250, 100, 0.7);
    generateBuildings(800, 640, 120, 100, 0.7);

    return result;
  }, []);

  return (
    <div className="city-map-container">
      <svg
        ref={svgRef}
        viewBox="0 0 1200 800"
        className="city-map"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Definitionen für Gradienten und Filter */}
        <defs>
          {/* Straßen-Glow */}
          <filter id="road-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Auto-Glow */}
          <filter id="car-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Wasser-Gradient für Isar */}
          <linearGradient id="water-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#1a3a4a" />
            <stop offset="50%" stopColor="#2a5a6a" />
            <stop offset="100%" stopColor="#1a3a4a" />
          </linearGradient>

          {/* Park-Gradient */}
          <radialGradient id="park-gradient">
            <stop offset="0%" stopColor="#2a4a2a" />
            <stop offset="100%" stopColor="#1a3a1a" />
          </radialGradient>

          {/* Autobahn-Gradient */}
          <linearGradient id="highway-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3a5a8a" />
            <stop offset="50%" stopColor="#4a7aaa" />
            <stop offset="100%" stopColor="#3a5a8a" />
          </linearGradient>

          {/* Route-Pfade für Fahrzeuge (unsichtbar, nur für Animation) */}
          {routes.map(route => (
            <path key={route.id} id={`route-${route.id}`} d={route.path} fill="none" />
          ))}
        </defs>

        {/* Hintergrund - dunkler Stadtgrund */}
        <rect x="0" y="0" width="1200" height="800" fill="#12121e" />

        {/* Stadtbereiche mit Gebäuden */}
        <g className="city-blocks">
          {buildings}
        </g>

        {/* Parks und Grünflächen */}
        <g className="parks">
          <ellipse cx="850" cy="180" rx="60" ry="80" fill="url(#park-gradient)" opacity="0.7" />
          <circle cx="830" cy="160" r="8" fill="#3a5a3a" opacity="0.8" />
          <circle cx="860" cy="190" r="10" fill="#2a4a2a" opacity="0.8" />
          <circle cx="870" cy="150" r="6" fill="#4a6a4a" opacity="0.7" />

          <ellipse cx="150" cy="500" rx="50" ry="40" fill="url(#park-gradient)" opacity="0.6" />
          <circle cx="140" cy="490" r="7" fill="#3a5a3a" opacity="0.8" />
          <circle cx="165" cy="510" r="9" fill="#2a4a2a" opacity="0.8" />

          <path d="M 200 100 Q 250 80 280 120 Q 260 160 210 150 Q 180 130 200 100" fill="url(#park-gradient)" opacity="0.6" />

          <ellipse cx="450" cy="680" rx="35" ry="25" fill="url(#park-gradient)" opacity="0.5" />
        </g>

        {/* Isar - realistischer Flussverlauf */}
        <g className="isar">
          <path
            d="M 760 0 Q 740 100 755 200 Q 770 300 745 400 Q 720 500 750 600 Q 780 700 740 800"
            fill="none"
            stroke="#0a1a2a"
            strokeWidth="45"
            opacity="0.5"
          />
          <path
            d="M 760 0 Q 740 100 755 200 Q 770 300 745 400 Q 720 500 750 600 Q 780 700 740 800"
            fill="none"
            stroke="url(#water-gradient)"
            strokeWidth="35"
          />
          <path
            d="M 742 0 Q 722 100 737 200 Q 752 300 727 400 Q 702 500 732 600 Q 762 700 722 800"
            fill="none"
            stroke="#2a4a3a"
            strokeWidth="4"
            opacity="0.8"
          />
          <path
            d="M 778 0 Q 758 100 773 200 Q 788 300 763 400 Q 738 500 768 600 Q 798 700 758 800"
            fill="none"
            stroke="#2a4a3a"
            strokeWidth="4"
            opacity="0.8"
          />
          <rect x="720" y="195" width="60" height="10" fill="#4a4a5a" rx="2" />
          <rect x="710" y="395" width="70" height="12" fill="#4a4a5a" rx="2" />
          <rect x="730" y="595" width="55" height="10" fill="#4a4a5a" rx="2" />
        </g>

        {/* Nebenstraßen */}
        <g className="minor-roads">
          <path d="M 100 150 Q 150 150 200 180 L 200 300" stroke="#3a3a4a" strokeWidth="4" fill="none" />
          <path d="M 100 300 L 250 300 Q 280 300 280 330 L 280 450" stroke="#3a3a4a" strokeWidth="4" fill="none" />
          <line x1="180" y1="100" x2="180" y2="250" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="100" y1="450" x2="250" y2="450" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="220" y1="150" x2="220" y2="550" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="100" y1="550" x2="260" y2="550" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="150" y1="620" x2="150" y2="750" stroke="#3a3a4a" strokeWidth="3" />

          <line x1="950" y1="100" x2="950" y2="280" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="1020" y1="150" x2="1020" y2="350" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="880" y1="300" x2="1100" y2="300" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="880" y1="500" x2="1100" y2="500" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="950" y1="520" x2="950" y2="700" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="1020" y1="450" x2="1020" y2="650" stroke="#3a3a4a" strokeWidth="3" />

          <line x1="400" y1="180" x2="550" y2="180" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="400" y1="620" x2="550" y2="620" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="350" y1="250" x2="350" y2="550" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="480" y1="200" x2="480" y2="300" stroke="#3a3a4a" strokeWidth="3" />
          <line x1="480" y1="500" x2="480" y2="600" stroke="#3a3a4a" strokeWidth="3" />
        </g>

        {/* Mittlerer Ring */}
        <g className="major-roads outer-ring">
          <path
            d="M 600 100 Q 800 100 920 200 Q 1000 300 1000 400 Q 1000 500 920 600 Q 800 700 600 700 Q 400 700 280 600 Q 200 500 200 400 Q 200 300 280 200 Q 400 100 600 100"
            fill="none"
            stroke="#1a1a2a"
            strokeWidth="16"
          />
          <path
            d="M 600 100 Q 800 100 920 200 Q 1000 300 1000 400 Q 1000 500 920 600 Q 800 700 600 700 Q 400 700 280 600 Q 200 500 200 400 Q 200 300 280 200 Q 400 100 600 100"
            fill="none"
            stroke="#5a5a6a"
            strokeWidth="10"
          />
          <path
            d="M 600 100 Q 800 100 920 200 Q 1000 300 1000 400 Q 1000 500 920 600 Q 800 700 600 700 Q 400 700 280 600 Q 200 500 200 400 Q 200 300 280 200 Q 400 100 600 100"
            fill="none"
            stroke="#8a8a9a"
            strokeWidth="1"
            strokeDasharray="15 10"
          />
        </g>

        {/* Altstadtring */}
        <g className="major-roads inner-ring">
          <path
            d="M 600 280 Q 700 280 720 350 Q 740 420 720 490 Q 700 530 600 530 Q 500 530 480 490 Q 460 420 480 350 Q 500 280 600 280"
            fill="none"
            stroke="#1a1a2a"
            strokeWidth="12"
          />
          <path
            d="M 600 280 Q 700 280 720 350 Q 740 420 720 490 Q 700 530 600 530 Q 500 530 480 490 Q 460 420 480 350 Q 500 280 600 280"
            fill="none"
            stroke="#6a6a7a"
            strokeWidth="7"
          />
        </g>

        {/* Radiale Hauptstraßen */}
        <g className="major-roads radials">
          <line x1="600" y1="50" x2="600" y2="280" stroke="#1a1a2a" strokeWidth="12" />
          <line x1="600" y1="50" x2="600" y2="280" stroke="#5a5a6a" strokeWidth="7" />

          <line x1="600" y1="530" x2="600" y2="750" stroke="#1a1a2a" strokeWidth="12" />
          <line x1="600" y1="530" x2="600" y2="750" stroke="#5a5a6a" strokeWidth="7" />

          <line x1="50" y1="400" x2="280" y2="400" stroke="#1a1a2a" strokeWidth="12" />
          <line x1="50" y1="400" x2="280" y2="400" stroke="#5a5a6a" strokeWidth="7" />
          <line x1="280" y1="400" x2="468" y2="400" stroke="#1a1a2a" strokeWidth="10" />
          <line x1="280" y1="400" x2="468" y2="400" stroke="#5a5a6a" strokeWidth="6" />

          <line x1="732" y1="400" x2="920" y2="400" stroke="#1a1a2a" strokeWidth="10" />
          <line x1="732" y1="400" x2="920" y2="400" stroke="#5a5a6a" strokeWidth="6" />
          <line x1="920" y1="400" x2="1150" y2="400" stroke="#1a1a2a" strokeWidth="12" />
          <line x1="920" y1="400" x2="1150" y2="400" stroke="#5a5a6a" strokeWidth="7" />

          <path d="M 150 150 Q 215 175 280 200" stroke="#1a1a2a" strokeWidth="10" fill="none" />
          <path d="M 150 150 Q 215 175 280 200" stroke="#5a5a6a" strokeWidth="6" fill="none" />

          <path d="M 920 200 Q 985 175 1050 150" stroke="#1a1a2a" strokeWidth="10" fill="none" />
          <path d="M 920 200 Q 985 175 1050 150" stroke="#5a5a6a" strokeWidth="6" fill="none" />

          <path d="M 150 650 Q 215 625 280 600" stroke="#1a1a2a" strokeWidth="10" fill="none" />
          <path d="M 150 650 Q 215 625 280 600" stroke="#5a5a6a" strokeWidth="6" fill="none" />

          <path d="M 920 600 Q 985 625 1050 650" stroke="#1a1a2a" strokeWidth="10" fill="none" />
          <path d="M 920 600 Q 985 625 1050 650" stroke="#5a5a6a" strokeWidth="6" fill="none" />
        </g>

        {/* Autobahn A99 */}
        <g className="highway a99">
          <path d="M 35 50 Q 35 400 35 750" fill="none" stroke="#1a2a3a" strokeWidth="18" />
          <path d="M 35 50 Q 35 400 35 750" fill="none" stroke="url(#highway-gradient)" strokeWidth="14" />
          <path d="M 35 50 Q 35 400 35 750" fill="none" stroke="#7a9aba" strokeWidth="2" strokeDasharray="20 12" />

          <path d="M 1165 50 Q 1165 400 1165 750" fill="none" stroke="#1a2a3a" strokeWidth="18" />
          <path d="M 1165 50 Q 1165 400 1165 750" fill="none" stroke="url(#highway-gradient)" strokeWidth="14" />
          <path d="M 1165 50 Q 1165 400 1165 750" fill="none" stroke="#7a9aba" strokeWidth="2" strokeDasharray="20 12" />

          <path d="M 35 35 Q 300 25 600 35 Q 900 25 1165 35" fill="none" stroke="#1a2a3a" strokeWidth="18" />
          <path d="M 35 35 Q 300 25 600 35 Q 900 25 1165 35" fill="none" stroke="url(#highway-gradient)" strokeWidth="14" />
          <path d="M 35 35 Q 300 25 600 35 Q 900 25 1165 35" fill="none" stroke="#7a9aba" strokeWidth="2" strokeDasharray="20 12" />

          <path d="M 35 765 Q 300 775 600 765 Q 900 775 1165 765" fill="none" stroke="#1a2a3a" strokeWidth="18" />
          <path d="M 35 765 Q 300 775 600 765 Q 900 775 1165 765" fill="none" stroke="url(#highway-gradient)" strokeWidth="14" />
          <path d="M 35 765 Q 300 775 600 765 Q 900 775 1165 765" fill="none" stroke="#7a9aba" strokeWidth="2" strokeDasharray="20 12" />

          <circle cx="35" cy="35" r="20" fill="#2a3a4a" />
          <circle cx="1165" cy="35" r="20" fill="#2a3a4a" />
          <circle cx="35" cy="765" r="20" fill="#2a3a4a" />
          <circle cx="1165" cy="765" r="20" fill="#2a3a4a" />
        </g>

        {/* Autobahn A9 */}
        <g className="highway a9">
          <path d="M 600 35 L 600 100 Q 660 180 665 300 L 665 320" fill="none" stroke="#1a2a3a" strokeWidth="16" />
          <path d="M 600 35 L 600 100 Q 660 180 665 300 L 665 320" fill="none" stroke="url(#highway-gradient)" strokeWidth="12" />
          <path d="M 600 35 L 600 100 Q 660 180 665 300 L 665 320" fill="none" stroke="#7a9aba" strokeWidth="2" strokeDasharray="18 10" />

          {/* A9 Tunnel unter Marienplatz */}
          <line x1="665" y1="320" x2="665" y2="480" stroke="#1a2a3a" strokeWidth="14" />
          <line x1="665" y1="320" x2="665" y2="480" stroke="#2a3a4a" strokeWidth="10" strokeDasharray="8 6" />
          {/* Tunnel-Eingang Nord */}
          <rect x="655" y="312" width="20" height="12" fill="#1a2a3a" rx="2" />
          <path d="M 657 318 Q 665 310 673 318" fill="none" stroke="#3a4a5a" strokeWidth="2" />
          {/* Tunnel-Eingang Süd */}
          <rect x="655" y="476" width="20" height="12" fill="#1a2a3a" rx="2" />
          <path d="M 657 482 Q 665 490 673 482" fill="none" stroke="#3a4a5a" strokeWidth="2" />

          <path d="M 665 480 L 665 500 Q 660 620 600 700 L 600 765" fill="none" stroke="#1a2a3a" strokeWidth="16" />
          <path d="M 665 480 L 665 500 Q 660 620 600 700 L 600 765" fill="none" stroke="url(#highway-gradient)" strokeWidth="12" />
          <path d="M 665 480 L 665 500 Q 660 620 600 700 L 600 765" fill="none" stroke="#7a9aba" strokeWidth="2" strokeDasharray="18 10" />
        </g>

        {/* Marienplatz */}
        <g className="center">
          <rect x="575" y="385" width="50" height="30" fill="#3a3a4a" rx="3" />
          <rect x="580" y="390" width="40" height="20" fill="#4a4a5a" rx="2" />
        </g>

        {/* Fahrzeuge */}
        <g className="vehicles">
          {vehicles.map(vehicle => {
            const pos = getPositionOnPath(vehicle.routeId, vehicle.progress);
            if (!pos) return null;

            return (
              <g
                key={vehicle.id}
                className="vehicle"
                transform={`translate(${pos.x}, ${pos.y}) rotate(${pos.angle})`}
              >
                {/* Scheinwerfer-Effekt */}
                <circle
                  cx="0"
                  cy="0"
                  r="4"
                  fill={vehicle.color}
                  opacity="0.3"
                  filter="url(#car-glow)"
                />
                {/* Auto-Körper */}
                <rect
                  x="-6"
                  y="-3"
                  width="12"
                  height="6"
                  rx="2"
                  fill={vehicle.color}
                  stroke="#000"
                  strokeWidth="0.5"
                />
              </g>
            );
          })}
        </g>

        {/* Kreuzungen mit Ampeln - Punkte je nach Anzahl der Straßen */}
        <g className="intersections">
          {/* Innerer Ring - T-Kreuzungen */}
          {[
            { id: 'inner-n', x: 600, y: 280, dirs: ['n', 'e', 'w'] },  // Radiale von N, Ring O-W
            { id: 'inner-s', x: 600, y: 530, dirs: ['s', 'e', 'w'] },  // Radiale nach S, Ring O-W
            { id: 'inner-w', x: 468, y: 400, dirs: ['n', 's', 'w'] },  // Radiale von W, Ring N-S
            { id: 'inner-e', x: 732, y: 400, dirs: ['n', 's', 'e'] },  // Radiale nach O, Ring N-S
          ].map(({ id, x, y, dirs }) => {
            const colors = getTrafficLightColors(id);
            return (
              <g
                key={id}
                className="intersection"
                onClick={() => onIntersectionClick?.(id)}
                style={{ cursor: onIntersectionClick ? 'pointer' : 'default' }}
              >
                <rect x={x - 12} y={y - 12} width="24" height="24" fill="#2a2a3a" rx="4" />
                {dirs.includes('n') && <circle cx={x} cy={y - 6} r="4" fill={colors.ns} filter="url(#road-glow)" />}
                {dirs.includes('s') && <circle cx={x} cy={y + 6} r="4" fill={colors.ns} filter="url(#road-glow)" />}
                {dirs.includes('w') && <circle cx={x - 6} cy={y} r="4" fill={colors.ew} filter="url(#road-glow)" />}
                {dirs.includes('e') && <circle cx={x + 6} cy={y} r="4" fill={colors.ew} filter="url(#road-glow)" />}
              </g>
            );
          })}

          {/* Äußerer Ring - 4-Wege Kreuzungen */}
          {[
            { id: 'outer-n', x: 600, y: 100 },
            { id: 'outer-s', x: 600, y: 700 },
            { id: 'outer-w', x: 200, y: 400 },
            { id: 'outer-e', x: 1000, y: 400 },
          ].map(({ id, x, y }) => {
            const colors = getTrafficLightColors(id);
            return (
              <g
                key={id}
                className="intersection"
                onClick={() => onIntersectionClick?.(id)}
                style={{ cursor: onIntersectionClick ? 'pointer' : 'default' }}
              >
                <rect x={x - 14} y={y - 14} width="28" height="28" fill="#2a2a3a" rx="5" />
                <circle cx={x} cy={y - 8} r="5" fill={colors.ns} filter="url(#road-glow)" />
                <circle cx={x} cy={y + 8} r="5" fill={colors.ns} filter="url(#road-glow)" />
                <circle cx={x - 8} cy={y} r="5" fill={colors.ew} filter="url(#road-glow)" />
                <circle cx={x + 8} cy={y} r="5" fill={colors.ew} filter="url(#road-glow)" />
              </g>
            );
          })}

          {/* Eck-Kreuzungen - T-Form mit diagonalen Richtungen */}
          {/* outer-nw: Ring von NO, Ring nach S, Diagonale von NW */}
          {(() => {
            const colors = getCornerTrafficLightColors('outer-nw');
            return (
              <g
                className="intersection"
                onClick={() => onIntersectionClick?.('outer-nw')}
                style={{ cursor: onIntersectionClick ? 'pointer' : 'default' }}
              >
                <rect x={280 - 14} y={200 - 14} width="28" height="28" fill="#2a2a3a" rx="5" />
                {/* Ring von Nordost */}
                <circle cx={280 + 6} cy={200 - 6} r="5" fill={colors.ring} filter="url(#road-glow)" />
                {/* Ring nach Süd */}
                <circle cx={280} cy={200 + 8} r="5" fill={colors.ring} filter="url(#road-glow)" />
                {/* Diagonale von Nordwest */}
                <circle cx={280 - 6} cy={200 - 6} r="5" fill={colors.diagonal} filter="url(#road-glow)" />
              </g>
            );
          })()}

          {/* outer-ne: Ring von NW, Ring nach S, Diagonale von NO */}
          {(() => {
            const colors = getCornerTrafficLightColors('outer-ne');
            return (
              <g
                className="intersection"
                onClick={() => onIntersectionClick?.('outer-ne')}
                style={{ cursor: onIntersectionClick ? 'pointer' : 'default' }}
              >
                <rect x={920 - 14} y={200 - 14} width="28" height="28" fill="#2a2a3a" rx="5" />
                {/* Ring von Nordwest */}
                <circle cx={920 - 6} cy={200 - 6} r="5" fill={colors.ring} filter="url(#road-glow)" />
                {/* Ring nach Süd */}
                <circle cx={920} cy={200 + 8} r="5" fill={colors.ring} filter="url(#road-glow)" />
                {/* Diagonale von Nordost */}
                <circle cx={920 + 6} cy={200 - 6} r="5" fill={colors.diagonal} filter="url(#road-glow)" />
              </g>
            );
          })()}

          {/* outer-sw: Ring von N, Ring nach SO, Diagonale von SW */}
          {(() => {
            const colors = getCornerTrafficLightColors('outer-sw');
            return (
              <g
                className="intersection"
                onClick={() => onIntersectionClick?.('outer-sw')}
                style={{ cursor: onIntersectionClick ? 'pointer' : 'default' }}
              >
                <rect x={280 - 14} y={600 - 14} width="28" height="28" fill="#2a2a3a" rx="5" />
                {/* Ring von Nord */}
                <circle cx={280} cy={600 - 8} r="5" fill={colors.ring} filter="url(#road-glow)" />
                {/* Ring nach Südost */}
                <circle cx={280 + 6} cy={600 + 6} r="5" fill={colors.ring} filter="url(#road-glow)" />
                {/* Diagonale von Südwest */}
                <circle cx={280 - 6} cy={600 + 6} r="5" fill={colors.diagonal} filter="url(#road-glow)" />
              </g>
            );
          })()}

          {/* outer-se: Ring von N, Ring nach SW, Diagonale von SO */}
          {(() => {
            const colors = getCornerTrafficLightColors('outer-se');
            return (
              <g
                className="intersection"
                onClick={() => onIntersectionClick?.('outer-se')}
                style={{ cursor: onIntersectionClick ? 'pointer' : 'default' }}
              >
                <rect x={920 - 14} y={600 - 14} width="28" height="28" fill="#2a2a3a" rx="5" />
                {/* Ring von Nord */}
                <circle cx={920} cy={600 - 8} r="5" fill={colors.ring} filter="url(#road-glow)" />
                {/* Ring nach Südwest */}
                <circle cx={920 - 6} cy={600 + 6} r="5" fill={colors.ring} filter="url(#road-glow)" />
                {/* Diagonale von Südost */}
                <circle cx={920 + 6} cy={600 + 6} r="5" fill={colors.diagonal} filter="url(#road-glow)" />
              </g>
            );
          })()}
        </g>

        {/* Beschriftungen */}
        <g className="labels">
          <text x="600" y="408" textAnchor="middle" fill="#9a9aaa" fontSize="11" fontWeight="500">
            Marienplatz
          </text>

          <text x="540" y="310" textAnchor="end" fill="#6a6a7a" fontSize="9">
            Altstadtring
          </text>
          <text x="440" y="140" textAnchor="middle" fill="#6a6a7a" fontSize="9">
            Mittlerer Ring
          </text>

          <text x="20" y="400" textAnchor="start" fill="#5a7a9a" fontSize="11" fontWeight="bold">
            A99
          </text>
          <text x="1180" y="400" textAnchor="end" fill="#5a7a9a" fontSize="11" fontWeight="bold">
            A99
          </text>
          <text x="680" y="270" textAnchor="start" fill="#5a7a9a" fontSize="10" fontWeight="bold">
            A9
          </text>

          <text x="780" y="350" textAnchor="start" fill="#4a7a6a" fontSize="10" fontStyle="italic">
            Isar
          </text>

          <text x="850" y="185" textAnchor="middle" fill="#4a6a4a" fontSize="8">
            Engl. Garten
          </text>

          <text x="150" y="400" textAnchor="middle" fill="#5a5a6a" fontSize="9">
            Pasing
          </text>
          <text x="1050" y="400" textAnchor="middle" fill="#5a5a6a" fontSize="9">
            Bogenhausen
          </text>
          <text x="600" y="80" textAnchor="middle" fill="#5a5a6a" fontSize="9">
            Schwabing
          </text>
          <text x="600" y="730" textAnchor="middle" fill="#5a5a6a" fontSize="9">
            Sendling
          </text>
        </g>

        {/* Nordpfeil */}
        <g className="compass" transform="translate(1120, 80)">
          <circle cx="0" cy="0" r="20" fill="#2a2a3a" opacity="0.8" />
          <polygon points="0,-15 -5,5 0,0 5,5" fill="#8a8a9a" />
          <text x="0" y="-8" textAnchor="middle" fill="#aaa" fontSize="8" fontWeight="bold">N</text>
        </g>
      </svg>
    </div>
  );
}
